import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api, STATUS_LABELS, STATUS_COLORS, STATUS_ORDER, money, fmtDateTime } from "../lib/api";
import { Plus, MagnifyingGlass, ArrowUpRight, Funnel } from "@phosphor-icons/react";
import NewTicketWizard from "../components/NewTicketWizard";
import { InitialsBadge } from "../components/InitialsBadge";
import DateRangeFilter, { isInRange } from "../components/DateRangeFilter";
import { prefetchWizardCatalog } from "../lib/catalogCache";

export default function RepairTickets() {
  const [params, setParams] = useSearchParams();
  const activeStatus = params.get("status") || "all";
  const mine = params.get("assigned") === "me";
  const [mineCount, setMineCount] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({}); // server-side counts by status
  const [hasMore, setHasMore] = useState(false);
  const [q, setQ] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ mode: "all", from: null, to: null });

  const PAGE_SIZE = 100;

  const load = () => {
    setLoading(true);
    const listParams = { limit: PAGE_SIZE, skip: 0 };
    if (q) listParams.q = q;
    if (activeStatus !== "all") listParams.status = activeStatus;
    if (mine) listParams.assigned = "me";
    const countParams = {};
    if (q) countParams.q = q;
    if (mine) countParams.assigned = "me";
    Promise.all([
      api.get("/repair-tickets", { params: listParams }),
      api.get("/repair-tickets/count", { params: countParams }),
    ])
      .then(([listRes, countRes]) => {
        setTickets(listRes.data || []);
        setTotal(countRes.data?.total || 0);
        setStatusCounts(countRes.data?.by_status || {});
        setMineCount(countRes.data?.assigned_to_me || 0);
        setHasMore((listRes.data || []).length >= PAGE_SIZE);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    prefetchWizardCatalog().catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, activeStatus, mine]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const listParams = { limit: PAGE_SIZE, skip: tickets.length };
      if (q) listParams.q = q;
      if (activeStatus !== "all") listParams.status = activeStatus;
      if (mine) listParams.assigned = "me";
      const r = await api.get("/repair-tickets", { params: listParams });
      const next = r.data || [];
      setTickets((prev) => [...prev, ...next]);
      setHasMore(next.length >= PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  };

  const inDate = (t) => isInRange(t.created_at, dateRange);

  // Filter rail counts use server data when there's no local date-range
  // filter. With date-range active we count the loaded subset so the
  // numbers match the visible rows.
  const hasDateFilter = dateRange.mode !== "all";
  const counts = useMemo(() => {
    if (!hasDateFilter) {
      const c = { all: total };
      STATUS_ORDER.forEach((s) => { c[s] = statusCounts[s] || 0; });
      return c;
    }
    const c = { all: 0 };
    STATUS_ORDER.forEach((s) => { c[s] = 0; });
    tickets.forEach((t) => {
      if (!inDate(t)) return;
      c.all++;
      if (c[t.status] !== undefined) c[t.status]++;
    });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, dateRange, total, statusCounts, hasDateFilter]);

  const filtered = useMemo(() => {
    // Status is server-filtered; only date-range narrows locally.
    return tickets.filter((t) => inDate(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, dateRange]);

  const toggleMine = () => {
    const np = new URLSearchParams(params);
    if (mine) np.delete("assigned"); else np.set("assigned", "me");
    setParams(np);
  };

  const setStatus = (s) => {
    const np = new URLSearchParams(params);
    if (s === "all") np.delete("status"); else np.set("status", s);
    setParams(np);
  };

  return (
    <Layout>
      <PageHeader
        overline="REPAIR TICKETS"
        title="Workshop queue"
        actions={
          <>
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
            <div className="relative w-full sm:w-auto">
              <MagnifyingGlass size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input
                data-testid="repairs-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search ticket # / customer / IMEI"
                className="w-full sm:w-72 h-11 pl-10 pr-4 border border-[#E5E7EB] bg-white focus:border-[#121216] focus:outline-none text-[13px] rounded-full placeholder:text-[#9CA3AF] transition-colors"
              />
            </div>
            <button
              onMouseEnter={() => prefetchWizardCatalog().catch(() => {})}
              onFocus={() => prefetchWizardCatalog().catch(() => {})}
              onClick={() => {
                prefetchWizardCatalog().catch(() => {});
                setWizardOpen(true);
              }}
              data-testid="new-ticket-btn"
              className="h-11 px-5 rounded-full bg-[#121216] text-white text-[13px] font-semibold flex items-center gap-2 hover:bg-[#1E5FEB] transition-colors active:scale-[0.98]"
            >
              <Plus size={14} weight="bold" /> New ticket
            </button>
          </>
        }
      />

      <div className="px-4 sm:px-6 md:px-8 pb-10 pt-2">
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 sm:gap-6">
          {/* FILTER RAIL */}
          <aside className="space-y-6" data-testid="filter-rail">
            <div className="flex items-center gap-2 px-2">
              <Funnel size={13} weight="duotone" className="text-[#9CA3AF]" />
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
                Filter
              </div>
            </div>

            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-2 px-2">
              <button onClick={toggleMine} data-testid="filter-assigned-me"
                className={`shrink-0 lg:w-full flex items-center justify-between gap-3 h-10 px-4 rounded-xl text-[13px] font-semibold border transition-colors lg:mb-2 ${mine ? "bg-[#EFF6FF] border-[#1E5FEB] text-[#1E5FEB]" : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#BFDBFE]"}`}>
                <span className="flex items-center gap-2"><span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${mine ? "bg-[#1E5FEB] border-[#1E5FEB] text-white" : "border-slate-300"}`}>{mine ? "✓" : ""}</span>Assigned to me</span>
                <span className="font-mono text-[11px]">{mineCount}</span>
              </button>
              <FilterItem
                label="All tickets"
                count={counts.all}
                active={activeStatus === "all"}
                onClick={() => setStatus("all")}
                testid="filter-all"
              />
              {STATUS_ORDER.map((s) => (
                <FilterItem
                  key={s}
                  label={STATUS_LABELS[s]}
                  count={counts[s]}
                  dotColor={STATUS_COLORS[s]}
                  active={activeStatus === s}
                  onClick={() => setStatus(s)}
                  testid={`filter-${s}`}
                />
              ))}
            </nav>
          </aside>

          {/* LIST */}
          <section
            className="rounded-3xl border border-[#E5E7EB] bg-white overflow-hidden"
            style={{ boxShadow: "0 1px 0 rgba(17, 17, 17, 0.02)" }}
          >
            {/* Column header — hidden on mobile */}
            <div className="hidden md:grid grid-cols-[150px_1.4fr_1.5fr_130px_1fr_100px_32px] gap-4 px-6 py-4 border-b border-[#F3F4F6] text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
              <div>Ticket</div>
              <div>Customer</div>
              <div>Device & issue</div>
              <div>Assigned</div>
              <div>Status</div>
              <div className="text-right">Estimate</div>
              <div />
            </div>

            {/* Rows */}
            {loading && filtered.length === 0 ? (
              <ul className="divide-y divide-[#F3F4F6]" data-testid="repairs-skeleton">
                {Array.from({ length: 6 }).map((_, i) => (
                  <li key={i} className="hidden md:grid grid-cols-[150px_1.4fr_1.5fr_130px_1fr_100px_32px] gap-4 px-6 py-5 items-center">
                    <div className="h-3 w-14 rounded bg-[#F3F4F6] animate-pulse" />
                    <div className="space-y-1.5">
                      <div className="h-3.5 w-40 rounded bg-[#F3F4F6] animate-pulse" />
                      <div className="h-2.5 w-28 rounded bg-[#F3F4F6] animate-pulse" />
                    </div>
                    <div className="space-y-1.5">
                      <div className="h-3.5 w-48 rounded bg-[#F3F4F6] animate-pulse" />
                      <div className="h-2.5 w-36 rounded bg-[#F3F4F6] animate-pulse" />
                    </div>
                    <div className="h-6 w-24 rounded-full bg-[#F3F4F6] animate-pulse" />
                    <div className="h-5 w-20 rounded-full bg-[#F3F4F6] animate-pulse" />
                    <div className="h-4 w-14 rounded bg-[#F3F4F6] animate-pulse justify-self-end" />
                    <div />
                  </li>
                ))}
                {/* Mobile skeleton */}
                {Array.from({ length: 4 }).map((_, i) => (
                  <li key={`m-${i}`} className="md:hidden px-4 py-4">
                    <div className="h-4 w-32 rounded bg-[#F3F4F6] animate-pulse mb-2" />
                    <div className="h-3 w-48 rounded bg-[#F3F4F6] animate-pulse mb-1" />
                    <div className="h-3 w-24 rounded bg-[#F3F4F6] animate-pulse" />
                  </li>
                ))}
              </ul>
            ) : filtered.length === 0 ? (
              <div className="px-6 py-20 text-center">
                <div className="text-sm text-[#4B5563]">No tickets match this filter.</div>
                <div className="text-[11px] text-[#9CA3AF] mt-1">Try "All tickets" or start a new one.</div>
              </div>
            ) : (
              <ul className="divide-y divide-[#F3F4F6]">
                {filtered.map((t) => (
                  <li key={t.id}>
                    {/* Desktop row */}
                    <Link
                      to={`/repairs/${t.id}`}
                      data-testid={`row-${t.ticket_number}`}
                      className="hidden md:grid group grid-cols-[150px_1.4fr_1.5fr_130px_1fr_100px_32px] gap-4 px-6 py-5 items-center hover:bg-[#F8FAFC] transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-[#4B5563]">{t.ticket_number}</div>
                        <div className="text-[11px] text-[#4B5563] mt-0.5 leading-snug" data-testid={`row-created-${t.ticket_number}`}>
                          <div>{fmtDateTime(t.created_at)}</div>
                          {t.created_by_name && <div className="font-medium break-words">by {t.created_by_name}</div>}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-[#121216] truncate">
                          {t.customer_name}
                        </div>
                        {t.customer_phone && (
                          <div className="text-[11px] text-[#9CA3AF] font-mono truncate mt-0.5">
                            {t.customer_phone}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-[#121216] truncate">
                          {t.device_make} {t.device_model}
                          {Array.isArray(t.additional_devices) && t.additional_devices.length > 0 && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-blue-50 text-[#1E5FEB] border border-blue-200" title={`Plus ${t.additional_devices.map(d => `${d.device_make} ${d.device_model}`).join(", ")}`}>
                              +{t.additional_devices.length}
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] text-[#4B5563] truncate mt-0.5">{t.issue}</div>
                      </div>
                      <div className="min-w-0" data-testid={`row-assigned-${t.ticket_number}`}>
                        {t.assigned_to_name ? (
                          <span className="flex items-center gap-2 min-w-0"><InitialsBadge name={t.assigned_to_name} size={24} /><span className="text-[12.5px] font-medium text-[#121216] truncate">{t.assigned_to_name}</span></span>
                        ) : <span className="text-[12px] text-[#9CA3AF]">—</span>}
                      </div>
                      <div>
                        <StatusPill status={t.status} />
                      </div>
                      <div className="text-right">
                        <span className="font-display text-[15px] font-semibold tabular-nums text-[#121216]">
                          ${money(t.estimated_cost)}
                        </span>
                      </div>
                      <div className="flex justify-end">
                        <ArrowUpRight
                          size={14}
                          weight="bold"
                          className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                    </Link>
                    {/* Mobile card */}
                    <Link
                      to={`/repairs/${t.id}`}
                      data-testid={`row-mobile-${t.ticket_number}`}
                      className="md:hidden flex items-center gap-3 px-4 py-3.5 hover:bg-[#F8FAFC] active:bg-[#F0F4FF] transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-mono text-[10px] text-[#9CA3AF]">{t.ticket_number}</span>
                          <StatusPill status={t.status} />
                        </div>
                        <div className="text-[14px] font-semibold text-[#121216] truncate">{t.customer_name}</div>
                        <div className="text-[12px] text-[#4B5563] truncate mt-0.5">
                          {t.device_make} {t.device_model}{t.issue ? ` — ${t.issue}` : ""}
                        </div>
                        <div className="text-[11px] text-[#4B5563] mt-0.5" data-testid={`row-mobile-created-${t.ticket_number}`}>
                          {fmtDateTime(t.created_at)}{t.created_by_name ? ` · by ${t.created_by_name}` : ""}{t.assigned_to_name ? ` · assigned to ${t.assigned_to_name}` : ""}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="font-display text-[15px] font-semibold tabular-nums text-[#121216]">
                          ${money(t.estimated_cost)}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {/* Footer count + server-side load-more */}
            {filtered.length > 0 && (
              <div className="px-6 py-3 border-t border-[#F3F4F6] text-[11px] text-[#9CA3AF] font-mono flex items-center justify-between">
                <span>
                  {hasDateFilter
                    ? `${filtered.length} ${filtered.length === 1 ? "ticket" : "tickets"} in range`
                    : total > tickets.length
                      ? `Showing ${tickets.length} of ${total.toLocaleString()} · latest first`
                      : `${total} ${total === 1 ? "ticket" : "tickets"}`}
                </span>
                {hasMore && !hasDateFilter && (
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    data-testid="repairs-load-more"
                    className="text-[11px] font-semibold tracking-wider uppercase text-[#1E5FEB] hover:text-[#1849C8] disabled:text-[#9CA3AF] transition-colors"
                  >
                    {loadingMore ? "LOADING…" : `LOAD ${PAGE_SIZE} MORE →`}
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      <NewTicketWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onCreated={load} />
    </Layout>
  );
}

function FilterItem({ label, count, dotColor, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-colors shrink-0 ${
        active
          ? "bg-[#121216] text-white"
          : "text-[#4B5563] hover:bg-[#F8FAFC] hover:text-[#121216]"
      }`}
    >
      {dotColor && (
        <span
          className="w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: active ? "#FFFFFF" : dotColor }}
        />
      )}
      {!dotColor && <span className="w-1.5 h-1.5 shrink-0" />}
      <span className="flex-1 text-left font-medium whitespace-nowrap">{label}</span>
      <span className={`text-[11px] tabular-nums font-mono ${active ? "text-white/60" : "text-[#9CA3AF]"}`}>
        {count}
      </span>
    </button>
  );
}

function StatusPill({ status }) {
  return (
    <span
      className="text-[10px] tracking-[0.06em] uppercase px-2.5 py-1 rounded-full font-semibold inline-block"
      style={{
        background: `${STATUS_COLORS[status]}15`,
        color: STATUS_COLORS[status],
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}
